import type { RecommendationsProps } from './Recommendations';
import type { ProductRecommendations as RecommendationsSchema } from '../../../amplienceTypes/schemas/imported/product-recommendations-schema';
import type { RecentlyViewedProduct } from '../../../utilities/helpers';
import type { ReactElement } from 'react';

import { useState, useEffect } from 'react';

import { RecommendationsComponent } from './RecommendationsComponent';
import { RVP_KEY } from '../../../utilities/constants/localStorageKeys';
import { useUserContext } from '../../../utilities/context/dynamic/UserContext';
import { useAppContext } from '../../../utilities/context/static/AppContext';
import { usePDPContext } from '../../../utilities/context/static/PDPContext';
import {
  getRecentlyViewedProductsFromLocalStorage,
  removeExpiredProducts,
} from '../../../utilities/helpers';

export default function RecentlyViewed(
  props: RecommendationsSchema & RecommendationsProps
): ReactElement {
  const { showRV, onReady } = props;
  const { country, locale } = useAppContext();
  const { product } = usePDPContext();
  const { commonData } = useUserContext();
  const [show, setShow] = useState(!!showRV);
  const [products, setProducts] = useState<RecentlyViewedProduct[]>([]);

  const removeRV = () => {
    setShow(false);
    localStorage.removeItem(RVP_KEY(country));
  };

  const excludeCurrentProduct = (products: RecentlyViewedProduct[], code: string) =>
    products.filter((p: RecentlyViewedProduct) => p.code?.toLowerCase() !== code?.toLowerCase());

  useEffect(() => {
    let isActive = true;

    if (!show) {
      onReady?.(false);
    } else {
      const localStorageProducts = getRecentlyViewedProductsFromLocalStorage(country);
      const visibleLocalStorageProducts = excludeCurrentProduct(localStorageProducts, product.code);

      setProducts(visibleLocalStorageProducts);
      onReady?.(visibleLocalStorageProducts.length > 0);

      removeExpiredProducts(localStorageProducts, commonData?.csrfToken, locale, country)
        .then(filteredProducts => {
          if (!isActive) {
            return;
          }

          const filtered = excludeCurrentProduct(filteredProducts, product.code);

          setProducts(filtered);
          onReady?.(filtered.length > 0);
        })
        .catch(() => {
          if (!isActive) {
            return;
          }

          const filtered = excludeCurrentProduct(localStorageProducts, product.code);

          setProducts(filtered);
          onReady?.(filtered.length > 0);
        });
    }

    return () => {
      isActive = false;
    };
  }, [commonData?.csrfToken, country, locale, onReady, product.code, show]);

  return show ? (
    <RecommendationsComponent
      {...props}
      recommendedProducts={products}
      removeRV={removeRV}
      showQuickView
    />
  ) : (
    <></>
  );
}
